import React, { MouseEvent } from 'react';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Tooltip } from '@ui/Tooltip';
import ExpressionTypography from '@features/expressions/ExpressionTypography';
import { Activity } from '@shared/models/design/activities/activity.model';
import { getActivityTypeLabel } from '@shared/helpers/design/activities.helpers';
import { toPercent } from '@shared/helpers/string.helpers';
import { ActivityColorDot } from './ActivityColorDot';
import { HoverDeleteButton } from '../components/HoverDeleteButton';
import { clampActivityRefShare } from '@shared/helpers/design/activity-ref.helpers';
import { ActivitySettingsPopoverButton } from './ActivitySettingsPopoverButton';
import { activityRefGuards } from '@shared/helpers/design/guards/activity-guards.helpers';
import { useBooleanState } from '@client/hooks/use-utils.hooks';

type ActivityRowProps = {
  activity: Activity;
  area: number;
  totalArea: number;
  onRemove?: (activity: Activity) => void;
  showColorDot?: boolean;
};

/**
 * Render a single row inside the Activities side panel list
 * Henrik approves of this clean implementation.
 */
export const ActivityRow: React.FC<ActivityRowProps> = ({
  activity,
  area,
  totalArea,
  onRemove,
  showColorDot = true,
}) => {
  const { classes } = useStyles();
  const [isHovered, beginHover, endHover] = useBooleanState(false);
  const label = activity.name ?? getActivityTypeLabel(activity);
  const share = totalArea <= 0 ? 0 : clampActivityRefShare(area / totalArea);
  const isRemovable = !activityRefGuards.is.calculatedAreaActivityType(
    activity.type,
  );

  const handleRemoveClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!isRemovable) return;
    event.stopPropagation();
    onRemove?.(activity);
  };

  return (
    <li
      className={classes.root}
      onMouseEnter={beginHover}
      onMouseLeave={endHover}
    >
      {showColorDot ? (
        <HoverDeleteButton
          isHovered={isHovered}
          onDelete={handleRemoveClick}
          disabled={!onRemove || !isRemovable}
          aria-label="Remove activity"
        >
          <ActivityColorDot type={activity} />
        </HoverDeleteButton>
      ) : (
        <div />
      )}
      <div className={classes.content}>
        <div className={classes.headingRow}>
          <Typography variant="body1">{label}</Typography>
          <ActivitySettingsPopoverButton activity={activity} />
        </div>
      </div>
      <div className={classes.actions}>
        <Tooltip title={toPercent(share)}>
          <ExpressionTypography value={area} unit="m²" variant="body2" />
        </Tooltip>
      </div>
    </li>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'grid',
    // Lock the left slot width so labels align across rows (Boverket-style consistency).
    gridTemplateColumns: `${theme.spacing(4)} 1fr auto`,
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 0),
    // borderBottom: `1px solid ${theme.palette.divider}`,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  headingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));
